import { createCommandLog, type CommandLog, type CommandActor, type AnyCommand } from './commandLog';

export interface StoreDispatcher<TCommand extends AnyCommand = AnyCommand> {
  /** Dispatch a typed command (for agent/programmatic use). */
  dispatch(command: TCommand, actor?: CommandActor): void;
  readonly commandLog: CommandLog;
}

/**
 * Wraps an xstate store with command logging.
 *
 * - Monkey-patches `store.send` so UI-triggered events are logged automatically.
 * - Returns a `StoreDispatcher` whose `dispatch()` logs with a custom actor and forwards to the store.
 */
export function createStoreDispatcher<TCommand extends AnyCommand>(
  store: { send: (event: any) => void },
): StoreDispatcher<TCommand> {
  const commandLog = createCommandLog();
  const defaultActor: CommandActor = { type: 'user' };

  // Monkey-patch store.send to intercept UI-triggered events
  const originalSend = store.send.bind(store);
  (store as any).send = (event: AnyCommand) => {
    commandLog.push({
      command: event,
      timestamp: Date.now(),
      actor: defaultActor,
    });
    originalSend(event);
  };

  function dispatch(command: TCommand, actor?: CommandActor): void {
    const resolvedActor = actor ?? defaultActor;
    commandLog.push({
      command: command,
      timestamp: Date.now(),
      actor: resolvedActor,
    });
    originalSend(command);
  }

  return { dispatch, commandLog };
}
